import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { initTracking } from "@/lib/tracking";

import Landing from "@/pages/Landing";
import Wireless from "@/pages/Wireless";
import SecondNumber from "@/pages/SecondNumber";
import AuthCallback from "@/pages/AuthCallback";
import PortSign from "@/pages/PortSign";
import Dashboard from "@/pages/Dashboard";
import Numbers from "@/pages/Numbers";
import MyNumbers from "@/pages/MyNumbers";
import Porting from "@/pages/Porting";
import Inbox from "@/pages/Inbox";
import Calls from "@/pages/Calls";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Referrals from "@/pages/Referrals";
import Voicemails from "@/pages/Voicemails";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import { Terms, Privacy } from "@/pages/Legal";
import Poster from "@/pages/Poster";
import Brand from "@/pages/Brand";
import Advertise from "@/pages/Advertise";
import Deals from "@/pages/Deals";
import AdsPoster from "@/pages/AdsPoster";
import Watch from "@/pages/Watch";
import Franchise from "@/pages/Franchise";
import FranchisePortal from "@/pages/FranchisePortal";
import Mobile from "@/pages/Mobile";
import Shop from "@/pages/Shop";
import Wallet from "@/pages/Wallet";
import Support from "@/pages/Support";
import AppShell from "@/components/AppShell";
import HomeButton from "@/components/HomeButton";

const Protected = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">
                Loading session…
            </div>
        );
    if (!user) return <Navigate to="/" replace />;
    return children;
};

function Router() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/wireless" element={<Wireless />} />
            <Route path="/second-number" element={<SecondNumber />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/port-sign/:token" element={<PortSign />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/poster" element={<Poster />} />
            <Route path="/brand" element={<Brand />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/ads-poster" element={<AdsPoster />} />
            <Route path="/watch" element={<Watch />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/franchise/portal" element={<FranchisePortal />} />
            <Route
                path="/dashboard"
                element={
                    <Protected>
                        <AppShell>
                            <Dashboard />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/support"
                element={
                    <Protected>
                        <AppShell>
                            <Support />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/wallet"
                element={
                    <Protected>
                        <AppShell>
                            <Wallet />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/shop"
                element={
                    <Protected>
                        <AppShell>
                            <Shop />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/numbers"
                element={
                    <Protected>
                        <AppShell>
                            <Numbers />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/my-numbers"
                element={
                    <Protected>
                        <AppShell>
                            <MyNumbers />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/porting"
                element={
                    <Protected>
                        <AppShell>
                            <Porting />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/inbox"
                element={
                    <Protected>
                        <AppShell>
                            <Inbox />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/calls"
                element={
                    <Protected>
                        <AppShell>
                            <Calls />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/billing"
                element={
                    <Protected>
                        <AppShell>
                            <Billing />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/billing/success"
                element={
                    <Protected>
                        <AppShell>
                            <BillingSuccess />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/referrals"
                element={
                    <Protected>
                        <AppShell>
                            <Referrals />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/voicemails"
                element={
                    <Protected>
                        <AppShell>
                            <Voicemails />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/profile"
                element={
                    <Protected>
                        <AppShell>
                            <Profile />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/advertise"
                element={
                    <Protected>
                        <AppShell>
                            <Advertise />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/mobile"
                element={
                    <Protected>
                        <AppShell>
                            <Mobile />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/admin"
                element={
                    <Protected>
                        <AppShell>
                            <Admin />
                        </AppShell>
                    </Protected>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <HomeButton />
        </>
    );
}

function App() {
    React.useEffect(() => {
        api.get("/marketing/config").then(({ data }) => initTracking(data)).catch(() => {});
    }, []);
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Router />
                    <Toaster position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
